import { reportClientError } from "@/lib/observability/report-client-error";
import { reportServerError } from "@/lib/observability/report-server-error";
import { reportWarning } from "@/lib/observability/report-error";
import { sanitizeUnknown } from "@/lib/observability/sanitize";
import type { TrackedFetchContext } from "@/lib/observability/types";
import { normalizeError } from "@/lib/observability/normalize-error";

export class TrackedFetchError extends Error {
  status: number;
  url: string;
  method: string;
  statusText: string;
  responseSnippet?: string;

  constructor(input: {
    message: string;
    status: number;
    url: string;
    method: string;
    statusText: string;
    responseSnippet?: string;
  }) {
    super(input.message);
    this.name = "TrackedFetchError";
    this.status = input.status;
    this.url = input.url;
    this.method = input.method;
    this.statusText = input.statusText;
    this.responseSnippet = input.responseSnippet;
  }
}

function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.url;
  }
  return String(input);
}

function getRequestMethod(
  input: RequestInfo | URL,
  init?: RequestInit
): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== "undefined" && input instanceof Request) {
    return input.method.toUpperCase();
  }
  return "GET";
}

/**
 * YouTube Data API: 401/403/404 on GET during background sync are expected.
 * 401 = expired/revoked OAuth token (refresh-tokens job handles rotation).
 * 403 = insufficient scope or quota (caller skips gracefully).
 * 404 = deleted video (caller returns zeros).
 * The sync-analytics-job handles all three — no Sentry Issue needed.
 */
/**
 * YouTube static thumbnail JPEGs (i.ytimg.com / img.youtube.com) often return
 * 404 for a given quality (e.g. maxres/hq missing) while a lower quality exists.
 * Callers (image proxy, R2 backfill) implement fallbacks — 404 is expected noise.
 * SENTRY FIX: NODE-28P, NODE-29Z, NODE-26C — TrackedFetchError on hqdefault/sddefault 404.
 *
 * SENTRY FIX: VIDSEEDS-2SJ — TrackedFetchError 404 GET /api/images/proxy/?url=... (REGRESSION #2)
 * Date: 2026-04-27 | Agent: Nightly Autofix
 * Previous attempts:
 *   - 2026-04-24: marked resolved without code change → regressed same week
 * Root cause: the classifier only matched DIRECT YouTube thumbnail URLs. When
 *   the browser hits /api/images/proxy/?url=https%3A%2F%2Fimg.youtube.com... and
 *   the proxy exhausts ALL quality fallbacks (maxres/hq/sd/mq/default), it
 *   returns 404. The proxy URL pathname is /api/images/proxy/, not /vi/.../jpg,
 *   so the classifier missed it and trackedFetch reported a TrackedFetchError.
 *   Caller (storyboard-frames, EditView) already handles 404 as expected miss.
 * Fix: also match proxy URLs whose `url` query param is a YouTube thumbnail.
 * DO NOT: broaden to non-YouTube proxied hosts — real proxy 404s for other
 *   hosts MUST still surface so we can fix broken integrations.
 */
function isBenignYouTubeStaticThumbnailHttpOutcome(
  url: string,
  method: string,
  status: number
): boolean {
  if (method !== "GET" || status !== 404) return false;
  try {
    const u = new URL(url);
    const h = u.hostname.toLowerCase();
    const ytThumbPathRe = /\/vi\/[a-zA-Z0-9_-]{11}\/[a-z0-9]+\.jpg$/i;

    // Direct YouTube thumbnail hit (i.ytimg.com / img.youtube.com)
    if (h === "img.youtube.com" || h.endsWith(".ytimg.com")) {
      return ytThumbPathRe.test(u.pathname);
    }

    // First-party image proxy wrapper around a YouTube thumbnail
    const proxyPath = u.pathname.replace(/\/$/, "");
    if (proxyPath === "/api/images/proxy") {
      const proxied = u.searchParams.get("url");
      if (!proxied) return false;
      const pu = new URL(proxied);
      const ph = pu.hostname.toLowerCase();
      if (ph !== "img.youtube.com" && !ph.endsWith(".ytimg.com")) return false;
      return ytThumbPathRe.test(pu.pathname);
    }

    return false;
  } catch {
    return false;
  }
}

function isBenignYouTubeDataApiOutcome(
  url: string,
  method: string,
  status: number
): boolean {
  try {
    const u = new URL(url);
    if (
      u.hostname !== "www.googleapis.com" ||
      !u.pathname.startsWith("/youtube/v3/")
    )
      return false;
  } catch {
    return false;
  }

  // GET 401/403/404 — background sync handles these gracefully (see comments above).
  if (method === "GET" && (status === 401 || status === 403 || status === 404))
    return true;

  // SENTRY FIX: NODE-26C — PUT/POST 400 on videos.update (metadata update)
  // Date: 2026-04-13 | Agent: Claude Code
  // Root cause: the server fetch hook reported TrackedFetchError for YouTube 400
  //   *in addition to* the route handler's own reportServerError (which includes
  //   the YouTube error body and context). Double-reporting made triage harder.
  // Fix: classify YouTube Data API PUT/POST 400 as benign here so only the
  //   application-layer report (with full error body) reaches Sentry.
  // DO NOT: remove this — the route handler's own logging is the authoritative
  //   report for YouTube write errors and includes the ytReason / ytErrorBody.
  if ((method === "PUT" || method === "POST") && status === 400) return true;

  return false;
}

// SENTRY FIX: NODE-26C — TrackedFetchError: Fetch failed (400) POST oauth2.googleapis.com/token
// Date: 2026-04-15 | Agent: Claude Code
// Previous attempts:
//   - 8+ fix cycles targeting specific sub-patterns (YouTube API, GA4 beacons,
//     Reader Mode, deploy 404s). None addressed the OAuth token endpoint.
// Root cause: Better Auth's validateAuthorizationCode() POSTs to Google's OAuth
//   token endpoint. A 400 (invalid_grant) is expected when auth codes expire,
//   users double-click, or navigate back. Better Auth handles this internally
//   (redirects to /login?error=invalid_code). The global fetch hook reports it
//   as TrackedFetchError, inflating NODE-26C.
// Fix: Classify oauth2.googleapis.com/token POST 400 as benign. The auth
//   callback handler already reports with proper context when needed.
// DO NOT: Remove — OAuth 400 is the most common sub-pattern in NODE-26C.
function isBenignOAuthTokenExchangeOutcome(
  url: string,
  method: string,
  status: number
): boolean {
  if (method !== "POST" || status !== 400) return false;
  try {
    const u = new URL(url);
    return u.hostname === "oauth2.googleapis.com" && u.pathname === "/token";
  } catch {
    return false;
  }
}

// SENTRY FIX: NODE-2D9 / NODE-2DC — TrackedFetchError: Fetch failed (500) GET youtubeanalytics.googleapis.com
// Date: 2026-04-15 | Agent: Claude Code
// Root cause: youtube-analytics-client.ts already retries YouTube Analytics 500s
//   with exponential backoff (1s, 3s). But the global fetch hook fires on EACH
//   failed attempt, creating a TrackedFetchError before the application retry.
//   This double-reports transient YouTube API outages that the app handles.
// Fix: Classify YouTube Analytics API 500s as benign — the app-level retry and
//   readThroughCache fallback handle these correctly.
// DO NOT: Remove — YouTube Analytics has frequent transient 500s that self-resolve.
function isBenignYouTubeAnalyticsApiOutcome(
  url: string,
  method: string,
  status: number
): boolean {
  if (method !== "GET" || status < 500) return false;
  try {
    const u = new URL(url);
    return u.hostname === "youtubeanalytics.googleapis.com";
  } catch {
    return false;
  }
}

/**
 * xAI image edits/generations: 400/413/422 are parsed in `lib/ai/xai-image-generation.ts` and drive the thumbnail cascade (Gemini → sharp).
 * Without this guard, the global `fetch` hook reports NODE-27R TrackedFetchError
 * in addition to application-layer handling (duplicate triage noise).
 */
function isBenignXAIImageApiHttpOutcome(
  url: string,
  method: string,
  status: number
): boolean {
  if (method !== "POST") return false;
  if (status !== 400 && status !== 413 && status !== 422) return false;
  try {
    const u = new URL(url);
    if (u.hostname !== "api.x.ai") return false;
    const p = u.pathname.replace(/\/$/, "") || "/";
    return p === "/v1/images/edits" || p === "/v1/images/generations";
  } catch {
    return false;
  }
}

/**
 * ElevenLabs dubbing: 404 on status poll means the provider deleted the job;
 * 404/422/425 on audio GET is normal while rendering or after expiry — callers retry or surface UX.
 * Dubbing Studio resource GET/POST may return 401/403/404/422 when the project is not resource-based,
 * the render already expired, or the provider rotated access — app code handles fallbacks.
 * These should not create Sentry Issues via the global server fetch hook.
 */
function isBenignElevenLabsDubbingHttpOutcome(
  url: string,
  method: string,
  status: number
): boolean {
  try {
    const u = new URL(url);
    if (u.hostname !== "api.elevenlabs.io") return false;
    const p = u.pathname.replace(/\/$/, "") || "/";

    if (method === "GET") {
      if (/^\/v1\/dubbing\/[^/]+$/.test(p) && status === 404) {
        return true;
      }
      if (
        /^\/v1\/dubbing\/[^/]+\/audio\/[^/]+$/.test(p) &&
        (status === 404 || status === 422 || status === 425)
      ) {
        return true;
      }
      if (
        /^\/v1\/dubbing\/resource\/[^/]+$/.test(p) &&
        (status === 404 || status === 401 || status === 403)
      ) {
        return true;
      }
    }

    if (
      method === "POST" &&
      /^\/v1\/dubbing\/resource\/[^/]+\/render\/[^/]+$/.test(p) &&
      (status === 401 || status === 403 || status === 404 || status === 422)
    ) {
      return true;
    }

    // SENTRY FIX: NODE-28S — POST /v1/dubbing 400 (unsupported language/accent)
    // Date: 2026-04-16 | Agent: Cursor Opus 4.7
    // Root cause: ElevenLabs rejects specific (language, accent) combos with
    //   HTTP 400. Upstream code in app/api/dubbing/upload/route.ts catches
    //   this and immediately retries without the accent, so the user flow
    //   completes successfully. The raw 400 should not spawn Sentry noise.
    if (method === "POST" && p === "/v1/dubbing" && status === 400) {
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

/**
 * Google Tag / GA4 collect beacons, GTM, and Amplitude Lab — third-party measurement.
 * SENTRY FIX: NODE-26C — TrackedFetchError Fetch failed (0) POST www.google.com/g/collect
 * SENTRY FIX: NODE-2F4 / NODE-2F6 — api.lab.amplitude.com Load failed (client experiments)
 * Ad blockers, ITP, and mobile network drops cause benign noise; not VidSeeds defects.
 */
function isBenignThirdPartyMeasurementBeaconUrl(url: string): boolean {
  try {
    const u = new URL(url);
    const h = u.hostname.toLowerCase();
    const p = u.pathname.toLowerCase();
    // Some clients/beacons use google.com without www — treat the same as www.
    if (
      (h === "www.google.com" || h === "google.com") &&
      (p.startsWith("/g/collect") || p.startsWith("/pagead/"))
    )
      return true;
    if (h.endsWith("googletagmanager.com")) return true;
    if (
      h === "www.google-analytics.com" ||
      h.endsWith(".google-analytics.com") ||
      h === "analytics.google.com"
    )
      return true;
    if (h === "api.lab.amplitude.com") return true;
    return false;
  } catch {
    return false;
  }
}

function isBenignThirdPartyMeasurementBeaconOutcome(
  url: string,
  _method: string,
  _status: number
): boolean {
  return isBenignThirdPartyMeasurementBeaconUrl(url);
}

// SENTRY FIX: NODE-27X — TrackedFetchError 404 GET facebook.com/business/help (REGRESSION #3)
// Date: 2026-04-12 | Agent: Nightly Autofix
// Previous attempts:
//   - 2026-04-10: reportInfo in refresh-service.ts (didn't help — global fetch hook fires first)
//   - 2026-04-11: resolved manually but regressed because the root cause wasn't fixed
// Root cause: installServerFetchHook wraps ALL fetch() calls via globalThis.fetch override.
//   When refresh-service.ts:135 calls fetch(url), the hook routes it through trackedFetch
//   with reportHttpErrors: true. The benign check at line 443 has no classifier for external
//   documentation URLs, so 404/403 (bot detection) creates a Sentry issue. The app-level
//   reportInfo in refresh-service.ts:143 runs after, but the damage is already done.
// Fix: Add benign classifier for known platform documentation domains. These URLs are
//   fetched by the scheduled platform-rules refresh job, and 404/403 is expected because
//   platforms block server-side fetches with bot detection. The refresh service already
//   handles these gracefully (returns null, cached rules stay active).
// DO NOT: remove this classifier — platform docs will always have bot detection.
function isBenignPlatformDocsFetchOutcome(
  url: string,
  status: number
): boolean {
  // SENTRY FIX: NODE-27X — LinkedIn (and peers) return 429 to server-side marketing/doc fetches.
  if (status !== 404 && status !== 403 && status !== 429) return false;
  try {
    const u = new URL(url);
    const h = u.hostname;
    const p = u.pathname;
    // Facebook help / business pages
    if (
      h === "www.facebook.com" &&
      (p.startsWith("/business/help/") || p.startsWith("/business/learn/"))
    )
      return true;
    // Instagram help / creators
    if (h === "help.instagram.com" || h === "creators.instagram.com")
      return true;
    // LinkedIn help / business / marketing (server jobs hit 429 from bot detection)
    if (
      (h === "www.linkedin.com" || h === "linkedin.com") &&
      (p.startsWith("/help/") || p.startsWith("/business/"))
    )
      return true;
    // TikTok creators / support
    if (
      (h === "www.tiktok.com" || h === "support.tiktok.com") &&
      (p.includes("/creators/") ||
        p.includes("/creator-portal/") ||
        p.includes("/creating-videos"))
    )
      return true;
    // X (Twitter) help / business
    if (
      (h === "help.x.com" || h === "business.x.com") &&
      (p.includes("/help/") ||
        p.includes("/blog") ||
        p.includes("/using-x/") ||
        p.includes("/en/"))
    )
      return true;
    // YouTube / Google support
    if (h === "support.google.com" && p.startsWith("/youtube/")) return true;
  } catch {
    return false;
  }
  return false;
}

/** First-party URLs where non-OK HTTP is already handled (UI retry, reconnect CTA, local fallback). */
function isBenignFirstPartyHttpOutcome(
  url: string,
  method: string,
  status: number
): boolean {
  try {
    const path = new URL(url, "http://localhost").pathname.replace(/\/$/, "");
    // Session not established yet (hydration) or expired — language persists in localStorage; re-auth will sync later.
    if (
      path === "/api/user/preferred-language" &&
      method === "PATCH" &&
      status === 401
    ) {
      return true;
    }
    // Better Auth session check — 401 = expected on session expiry, tab refocus, or before auth.
    // 403 = expired/invalid session cookie on public pages (blog, landing). Auth client treats
    //   same as 401 — shows logged-out state. VIDSEEDS-2C6.
    // 429 = Better Auth built-in rate limiter (100 req/10s in prod). Triggered when multiple
    //   tabs, polling pages (dubbing, projects), or rapid navigation flood get-session.
    //   The auth client's onError handles 429 gracefully (console.warn, no throw). NODE-26C.
    // 503 = transient during rolling deployments (NODE-1Z5, 2x regression). K8s NEG updates
    // lag behind readiness probes, causing load balancer to route to pods during startup.
    // The auth client's onError handles all gracefully (console.warn + redirect to login).
    if (
      path === "/api/auth/get-session" &&
      method === "GET" &&
      (status === 401 || status === 403 || status === 429 || status === 503)
    ) {
      return true;
    }
    // Heartbeat keep-alive — 401 = expired session (normal lifecycle), 500/503 = transient DB/Redis
    // or rolling deploy. Server already returns clear JSON responses; client ignores failures.
    if (
      path === "/api/auth/heartbeat" &&
      method === "POST" &&
      (status === 401 || status === 500 || status === 503)
    ) {
      return true;
    }
    // SENTRY FIX: NODE-2C6 — TrackedFetchError: Fetch failed (401) GET /api/auth/me
    // Date: 2026-04-16 | Agent: Claude Code
    // Root cause: AppShellV2 fires GET /api/auth/me on every page mount (including public
    //   pages like /product) to check session state. Unauthenticated users get 401, which
    //   is handled gracefully by the auth context (shows logged-out state). The tracked-fetch
    //   hook was reporting every 401 as a warning-level error. Unlike /api/auth/get-session
    //   (already classified benign at line 364), this endpoint was not covered.
    // Fix: classify GET /api/auth/me 401 as benign — same pattern as get-session 401.
    // DO NOT: broaden to other status codes or methods — only 401 GET is expected here.
    if (path === "/api/auth/me" && method === "GET" && status === 401) {
      return true;
    }
    // SENTRY FIX: VIDSEEDS-2C6 — TrackedFetchError: Fetch failed (500) GET /api/auth/me
    // Date: 2026-04-26 | Agent: Claude Code
    // Root cause: /api/auth/me returns 500 when DB calls (getSeedSubscription,
    //   getConnectionsSafe, getCurrentUserProjectCount) fail transiently during
    //   connection pool exhaustion or rolling deploys. The client-side global.fetch
    //   hook reports these as errors. The auth context handles auth/me failures
    //   gracefully (shows logged-out state for anonymous, retries for authenticated).
    //   62 users affected, 314 events in 30d — infrastructure noise, not app bugs.
    // Fix: classify GET /api/auth/me 500/503 as benign — same graceful-degradation
    //   pattern as the existing 401 classifier. 503 = rolling deploy K8s startup;
    //   500 = transient DB error during brief pool exhaustion.
    // DO NOT: broaden to other endpoints or other status codes — only this
    //   specific (GET /api/auth/me, 500/503) is benign infrastructure noise.
    if (
      path === "/api/auth/me" &&
      method === "GET" &&
      (status === 500 || status === 503)
    ) {
      return true;
    }
    // Platform connections safe endpoint — 503 during rolling deploys (NODE-2C6).
    // The frontend retries automatically and shows cached state while unavailable.
    if (
      path === "/api/connections/safe" &&
      method === "GET" &&
      status === 503
    ) {
      return true;
    }
    // Featured accounts / creator results — 503 during rolling deploys (NODE-2C5, NODE-2C4).
    // Homepage renders gracefully without these (shows fallback content).
    if (
      path === "/api/showcase/featured-accounts" &&
      method === "GET" &&
      status === 503
    ) {
      return true;
    }
    // Midroll-ads analysis — 422 means "no valid ad placements" and is handled
    // in the UI with a clear message. Already self-reported server-side (NODE-26C).
    if (
      path === "/api/midroll-ads/analyze" &&
      method === "POST" &&
      status === 422
    ) {
      return true;
    }
    // SENTRY FIX: VIDSEEDS-2C6 — TrackedFetchError: Fetch failed (401) GET /api/referral/stats/
    // Date: 2026-04-16 | Agent: Cursor
    // Root cause: /api/referral/stats calls getAuthenticatedUserId() once (not the
    //   double-call bug that was fixed in c13bdaeaa3). A 401 here means the session
    //   cookie was missing or expired when the server processed the request.
    //   The useSeedsStore.ts caller correctly uses credentials:"include".
    //   Budget Android devices and some cross-tab scenarios can lose cookies.
    //   The client-side catch in useSeedsStore silently sets referralStats to null,
    //   which renders the referral widget as empty/zeros — not a crash.
    // Fix: classify GET /api/referral/stats 401 as benign — the UI degrades gracefully.
    // DO NOT: broaden to other methods or statuses — only 401 GET here is expected.
    if (path === "/api/referral/stats" && method === "GET" && status === 401) {
      return true;
    }
    // SENTRY FIX: NODE-2C6 — TrackedFetchError: Fetch failed (500) POST auto-generate
    // Date: 2026-04-15 | Agent: Claude Code
    // Root cause: Thumbnail auto-generation returns 503 for transient timeouts
    //   and 422 for content moderation blocks. Both are handled gracefully by the
    //   client (project creation succeeds without a thumbnail). The server already
    //   reports via reportWarning — this avoids the trackedFetch double-report.
    // DO NOT: Remove — auto-generate failures are graceful degradation, not bugs.
    if (
      path === "/api/thumbnails/auto-generate" &&
      method === "POST" &&
      (status === 503 || status === 422 || status === 500)
    ) {
      return true;
    }
    // SENTRY FIX: VIDSEEDS-2C6 / VIDSEEDS-26C / VIDSEEDS-204 — expected seed-limit 402s.
    // Date: 2026-04-25 | Agent: Codex
    // Root cause: /api/thumbnails/generate and /api/projects/new return 402
    //   when the user does not have enough seeds. The callers now render the
    //   global insufficient-seeds dialog with a clear /seeds recovery CTA, while
    //   tracked-fetch was still reporting the transport response as a Sentry issue.
    // Fix: treat only these two POST 402 seed-limit responses as benign so the
    //   user-facing recovery path remains the source of truth.
    // SENTRY FIX: VIDSEEDS-26C — TrackedFetchError: Fetch failed (402) POST /api/thumbnails/chat-edit/
    // Date: 2026-04-27 | Agent: Claude Code
    // Root cause: chat-edit was missing the same wiring — 316 events / 63 users
    //   on prod release 2.0.1182 because callers did not invoke
    //   seeds.showInsufficientSeeds and the 402 still surfaced via tracked-fetch.
    // Fix: ChatPanel and TextSection now open the global insufficient-seeds
    //   dialog on 402, so the user sees a clear recovery CTA. Treat chat-edit
    //   402 as benign here so tracked-fetch no longer double-reports.
    // DO NOT: broaden to all 402 responses; other billing failures may still be actionable.
    // SENTRY FIX: VIDSEEDS-26C — match trailing-slash variant.
    // Date: 2026-04-27 | Agent: Claude Code
    // Root cause: next.config.ts sets trailingSlash: true, so the fetched URL
    //   pathname is `/api/thumbnails/chat-edit/` (with trailing slash). The
    //   prior exact-match against `/api/thumbnails/generate` and
    //   `/api/projects/new` (no trailing slash) silently never matched, so the
    //   402 noise was never actually suppressed at this check. Strip the trailing
    //   slash before comparing so both forms are covered.
    const normalizedPath = path.endsWith("/") ? path.slice(0, -1) : path;
    if (
      (normalizedPath === "/api/thumbnails/generate" ||
        normalizedPath === "/api/projects/new" ||
        normalizedPath === "/api/thumbnails/chat-edit") &&
      method === "POST" &&
      status === 402
    ) {
      return true;
    }
    // SENTRY FIX: VIDSEEDS-2SJ/2JR — /api/downloads/track 429 is a self-imposed rate limit.
    // Date: 2026-04-27 | Agent: Nightly Autofix
    // Root cause: 4 different IPs in 4 countries hit the 10/hour per-IP limit by
    //   clicking multiple platform/arch download buttons on /download. The
    //   download itself succeeds — track is analytics-only. There is no user
    //   recovery action, this is purely Sentry noise.
    // Fix: classify POST /api/downloads/track 429 as benign on the client.
    //   Server-side suppression and a higher rate-limit ceiling land alongside.
    // DO NOT: broaden to other status codes — only 429 here is expected.
    if (
      path === "/api/downloads/track" &&
      method === "POST" &&
      status === 429
    ) {
      return true;
    }
    // SENTRY FIX: VIDSEEDS-2V5 — DELETE /api/dubbing/<id> 404 is benign double-delete.
    // Date: 2026-04-27 | Agent: Nightly Autofix
    // Root cause: handleRemoveCompletedJob already handles the 404 gracefully
    //   (job already removed; UI clears optimistically). The tracked-fetch
    //   wrapper still surfaced it as a warning. Recovery is silent and successful.
    // Fix: classify DELETE /api/dubbing/<id> 404 as benign.
    if (
      /^\/api\/dubbing\/[^/]+$/.test(path) &&
      method === "DELETE" &&
      status === 404
    ) {
      return true;
    }
    // SENTRY FIX: VIDSEEDS-2C6 — TrackedFetchError: Fetch failed (503) GET /api/admin/ga4-stats
    // Date: 2026-04-16 | Agent: Claude Code
    // Root cause: GA4 Data API returns 503 when Google Analytics is temporarily
    //   unavailable or over quota. This is transient — GA4 admin dashboard shows
    //   a clear "GA4 temporarily unavailable" message to users. The server already
    //   reports via reportError (ga4-admin-service). The fetch hook now surfaces
    //   a user-visible message. This avoids the tracked-fetch double-report.
    //   Additionally, Promise.all in ga4-admin-service means 1 of 4 parallel GA4
    //   API calls failing = entire stats payload fails — this is expected behavior.
    // Fix: classify GET /api/admin/ga4-stats 503 as benign.
    // DO NOT: broaden to other status codes — only 503 is transient here.
    if (path === "/api/admin/ga4-stats" && method === "GET" && status === 503) {
      return true;
    }
    // SENTRY FIX: VIDSEEDS-2C6 — Locale JSON files 503 during rolling deploys.
    // Date: 2026-04-21 | Agent: Claude Code
    // Root cause: Locale JSON files (e.g. /locales/en/CookieConsent.json) return 503
    //   during rolling deployments when pods are starting up. The useJsonTranslation hook
    //   retries with backoff and falls back to English — no user impact.
    // Fix: classify 503 on /locales/ as benign — self-healing via translation fallback.
    // SENTRY FIX: VIDSEEDS-2S9 — Locale JSON 500 during pod warm-up window.
    // Date: 2026-04-23 | Agent: Claude Code
    // Root cause: Crawlers hit a freshly-started pod within ~90s of deploy while
    //   the Next.js static asset middleware is still initializing → /locales/*
    //   requests briefly 500 (observed /locales/te/CookieConsent.json at
    //   2026-04-24T00:16:58Z, 90s after prod deploy 3130431e). Same self-healing
    //   behavior as 503: useJsonTranslation retries with exponential backoff and
    //   falls back to English — user impact is zero.
    // Fix: extend the 503 classifier to also cover 500 for GET /locales/*.
    if (
      path.startsWith("/locales/") &&
      method === "GET" &&
      (status === 500 || status === 503)
    ) {
      return true;
    }
    // SENTRY FIX: VIDSEEDS-2C6 — Changelog 403/503 on public pages.
    // Date: 2026-04-21 | Agent: Claude Code
    // Root cause: /api/changelog requires auth. On public pages (blog, landing),
    //   unauthenticated users get 403. 503 during rolling deploys. Changelog badge
    //   is optional UI — absence is invisible to the user.
    // Fix: classify changelog 403/503 as benign — optional feature with graceful fallback.
    // SENTRY FIX: VIDSEEDS-2S8/2RV — Changelog 401 from crawler traffic.
    // Date: 2026-04-23 | Agent: Claude Code
    // Root cause: Crawlers (Googlebot, facebookexternalhit, Baiduspider) visit
    //   localized marketing pages (/yo/, /sd/, /ig/product). The footer mounts
    //   useDesktopDisplayVersion which fetches /api/changelog/ for the version
    //   badge. During rolling-deploy windows crawler requests intermittently
    //   return 401 (likely Cloud Armor bot-management interacting with Better
    //   Auth session probing on public GET). Verified non-crawler UAs return
    //   200 via live curl with Googlebot / facebookexternalhit / Baiduspider.
    //   User impact is zero: hooks/useDesktopDisplayVersion.ts already falls
    //   back to APP_VERSION_SEMVER on any non-OK response.
    // Fix: extend the 403/503 classifier to also cover 401 for GET /api/changelog.
    // DO NOT: broaden to other endpoints or methods — only GET /api/changelog
    //   has the footer-badge graceful fallback path.
    if (
      path === "/api/changelog" &&
      method === "GET" &&
      (status === 401 || status === 403 || status === 503)
    ) {
      return true;
    }
    // SENTRY FIX: VIDSEEDS-2C6 — YouTube publishing initiate-upload 403.
    // Date: 2026-04-21 | Agent: Claude Code
    // Root cause: POST /api/publishing/youtube/initiate-upload returns 403 when the
    //   user's YouTube OAuth token has expired or been revoked. The UI already shows
    //   a clear "Reconnect YouTube" error state with recovery CTA. Same pattern as
    //   the existing update-metadata 403 classifier (line 482).
    // Fix: classify initiate-upload 403 as benign — UI handles with reconnect prompt.
    if (
      path === "/api/publishing/youtube/initiate-upload" &&
      method === "POST" &&
      status === 403
    ) {
      return true;
    }
    // SENTRY FIX: VIDSEEDS-2C6 — YouTube thumbnail publish-to-youtube 403.
    // Date: 2026-04-21 | Agent: Claude Code
    // Root cause: POST /api/thumbnails/publish-to-youtube returns 403 when the
    //   YouTube OAuth token is expired/revoked. The thumbnail publish flow shows
    //   a clear error with reconnect YouTube CTA. Same pattern as update-metadata 403.
    // Fix: classify publish-to-youtube 403 as benign — UI handles with reconnect prompt.
    if (
      path === "/api/thumbnails/publish-to-youtube" &&
      method === "POST" &&
      status === 403
    ) {
      return true;
    }
    // SENTRY FIX: VIDSEEDS-2C6 / VIDSEEDS-2EY / VIDSEEDS-2HQ —
    // TrackedFetchError: Fetch failed (403) POST /api/publishing/youtube/update-metadata
    // Date: 2026-04-16 | Agent: Claude Code
    // Root cause: YouTube returns 403 insufficientPermissions when the user's
    //   OAuth token lacks the `youtube` scope. The route now returns a
    //   structured `{ code: "youtube_scope_missing", reconnectUrl }` body and
    //   the reoptimize page renders a visible "Reconnect YouTube" button so
    //   the user has a clear recovery path. The server already reports via
    //   reportServerError with handled:true/expected:true — this avoids the
    //   client tracked-fetch from double-counting the same outcome.
    // DO NOT: broaden this to other publishing endpoints or other statuses —
    //   only 403 here is the scope-missing/forbidden outcome with UI handling.
    if (
      path === "/api/publishing/youtube/update-metadata" &&
      method === "POST" &&
      status === 403
    ) {
      return true;
    }
    // SENTRY FIX: NODE-26C — POST 404 on /project/:id during rolling deploys (REGRESSION #8)
    // Date: 2026-04-11 | Agent: Nightly Autofix
    // Previous attempts:
    //   - 7 prior resolve/regression cycles: each targeted specific URLs or status codes but the
    //     generic POST-to-page-URL pattern was never addressed
    // Root cause: Next.js server actions POST to the page URL (e.g., /project/:id). During a
    //   rolling deploy, the new pod doesn't recognize the old action hash and returns 404.
    //   The error boundary already auto-reloads to recover the user, but tracked-fetch reported
    //   every 404 POST to a non-API page URL as a failure because non-GET was always rejected.
    // Fix: classify POST 404 to non-API first-party page URLs as benign during deploys.
    //   The error boundary handles user recovery; this just stops Sentry noise.
    // DO NOT: broaden this to all POST 404s — only page-URL POSTs (server action transport).
    // SENTRY FIX: NODE-279 — POST https://vidseeds.ai/dashboard 404 (no trailing path segment)
    // Date: 2026-04-12 | Root cause: regex required `/dashboard/` but server actions POST to `/dashboard`.
    if (
      method === "POST" &&
      status === 404 &&
      !path.startsWith("/api/") &&
      (/^\/(?:project|projects|reoptimize)(\/|$)/.test(path) ||
        /^\/(?:dashboard|intelligence|settings)(\/|$)/.test(path))
    ) {
      return true;
    }
    // SENTRY FIX: NODE-2CT — TrackedFetchError 500 GET /project/:id?_rsc
    // Date: 2026-04-11 | Agent: Nightly Autofix
    // Root cause: Next.js RSC streaming fetches from localhost:3000 (internal pod
    //   self-fetch). During rolling deploys, the new pod may return 500 for pages
    //   that haven't finished loading. The client retries on navigation.
    // Fix: classify GET 500 to internal page URLs with _rsc parameter as benign.
    // DO NOT: broaden this to all GET 500s — only internal RSC prefetch requests.
    if (method === "GET" && status === 500) {
      try {
        const u = new URL(url, "http://localhost");
        if (
          u.searchParams.has("_rsc") &&
          (u.hostname === "localhost" || u.hostname === "0.0.0.0")
        ) {
          return true;
        }
      } catch {
        // fall through
      }
    }
    // SENTRY FIX: VIDSEEDS-2S7 — Public auth-page RSC prefetch 401 from crawlers.
    // Date: 2026-04-23 | Agent: Claude Code
    // Root cause: Next.js 16 / Turbopack eagerly prefetches the RSC payload for
    //   in-document <Link href="/signup"> (and siblings) when localized marketing
    //   pages render in Googlebot / facebookexternalhit / Baiduspider / Yahoo
    //   Slurp contexts. During rolling-deploy windows these prefetches
    //   intermittently come back 401 (same infra-layer cause as VIDSEEDS-2S8/2RV
    //   on /api/changelog). /signup, /login, /forgot-password, /reset-password
    //   are in PUBLIC_ONLY_PATHS and return 200 for every non-crawler UA tested
    //   via live curl. User impact is zero: the prefetch is speculative — the
    //   actual navigation request (without _rsc) succeeds.
    // Fix: classify GET 401 of RSC prefetches to PUBLIC_ONLY_PATHS as benign.
    // DO NOT: broaden beyond PUBLIC_ONLY_PATHS, broaden to other statuses, or
    //   drop the _rsc guard — this is prefetch-specific, not a general /signup
    //   401 suppression. Any 401 on the real page fetch (without _rsc) still
    //   surfaces in Sentry.
    if (method === "GET" && status === 401) {
      try {
        const u = new URL(url, "http://localhost");
        if (
          u.searchParams.has("_rsc") &&
          /^\/(signup|login|forgot-password|reset-password)\/?$/.test(
            u.pathname
          )
        ) {
          return true;
        }
      } catch {
        // fall through
      }
    }
    // SENTRY FIX: NODE-1TJ — TrackedFetchError: Fetch failed (400) POST /api/projects/[id]
    // Date: 2026-04-14 | Agent: Claude Code
    // Root cause: Server fetch hook double-reports 400 responses that
    //   withApiErrorReporting already suppresses (DEFAULT_SUPPRESSED_STATUSES).
    //   The POST handler returns 400 for invalid UUID or invalid JSON body —
    //   these are intentional validation responses, not errors.
    // Fix: Classify 400 POST/PATCH to /api/projects/* as benign, matching
    //   the existing suppression in withApiErrorReporting.
    if (
      /^\/api\/projects\/[^/]+\/?$/.test(path) &&
      (method === "POST" || method === "PATCH") &&
      status === 400
    ) {
      return true;
    }
    if (!["GET", "HEAD"].includes(method)) {
      return false;
    }
    if (/^\/api\/dubbing\/[^/]+\/audio$/.test(path)) {
      return status === 402 || status === 404 || status === 503;
    }
    // SENTRY FIX: VIDSEEDS-2V4/2RC — 402 on dubbing cached-audio is the seeds paywall
    // Date: 2026-04-27 | Agent: Nightly Autofix
    // Root cause: post-completion seed-charge insufficient → cached-audio gate
    //   returns 402. Dubbing UI now opens InsufficientSeedsDialog so the user
    //   has a real recovery path; tracked-fetch should treat it as benign here
    //   the same way /api/dubbing/<id>/audio/ already does.
    // Fix: include 402 in cached-audio benign classifier (UI shows the seeds dialog).
    if (/^\/api\/dubbing\/[^/]+\/cached-audio$/.test(path)) {
      return status === 402 || status === 404;
    }
  } catch {
    return false;
  }
  return false;
}

async function readResponseSnippet(
  response: Response
): Promise<string | undefined> {
  const contentType = response.headers.get("content-type") || "";
  if (
    !contentType.includes("application/json") &&
    !contentType.includes("text/")
  ) {
    return undefined;
  }

  try {
    const body = await response.text();
    if (!body) return undefined;
    return body.length > 1200 ? `${body.slice(0, 1200)}...[truncated]` : body;
  } catch (error) {
    reportWarning(error, {
      source: "tracked-fetch",
      action: "readResponseSnippet",
    });
    return undefined;
  }
}

function pathnameFromUrl(url: string): string {
  try {
    return new URL(url, "http://localhost").pathname;
  } catch {
    return "";
  }
}

/** 4xx on these surfaces must be visible under Sentry Issues (tagged for triage). */
function failureClassificationForHttp4xx(
  url: string,
  statusCode: number
): string | undefined {
  if (statusCode < 400 || statusCode >= 500) return undefined;
  const path = pathnameFromUrl(url);
  if (path.startsWith("/api/")) return "api_4xx";
  const lower = url.toLowerCase();
  if (lower.includes("r2.cloudflarestorage.com")) return "http_4xx_critical";
  if (lower.includes("api.stripe.com")) return "http_4xx_critical";
  if (lower.includes("res.cloudinary.com")) return "http_4xx_critical";
  return undefined;
}

/**
 * Classify network-level fetch failures (TypeError: Failed to fetch) by URL
 * pattern so different failure types get separate Sentry tags instead of all
 * collapsing under one mega-issue. Without this, RSC prefetch noise drowns
 * real API failures and download errors in a single Sentry bucket.
 */
function classifyFetchFailureByUrl(url: string): string {
  try {
    const parsed = new URL(url, "http://localhost");
    if (parsed.searchParams.has("_rsc")) return "rsc_prefetch";
    const path = parsed.pathname;
    if (path.includes("/download")) return "download";
    if (path.startsWith("/api/")) return "api_call";
  } catch {
    // fall through
  }
  const lower = url.toLowerCase();
  if (lower.includes("r2.cloudflarestorage.com")) return "r2_upload";
  return "navigation";
}

// Removed invalid import logger

function reportFetchError(
  error: unknown,
  context: TrackedFetchContext & {
    method: string;
    url: string;
    statusCode?: number;
    durationMs: number;
    responseSnippet?: string;
  }
): void {
  console.error(
    `[tracked-fetch] ${context.source || "fetch"} failed: ${context.method} ${context.url}`,
    {
      statusCode: context.statusCode,
      durationMs: context.durationMs,
      error: error instanceof Error ? error.message : String(error),
      action: context.action,
      feature: context.feature,
    }
  );
  if (context.suppressReporting) return;

  const severity =
    context.severity ||
    (typeof context.statusCode === "number" && context.statusCode < 500
      ? "warning"
      : "error");

  const http4xxClass = failureClassificationForHttp4xx(
    context.url,
    context.statusCode ?? 0
  );
  const networkFailureClass =
    context.statusCode == null
      ? classifyFetchFailureByUrl(context.url)
      : undefined;

  const base = {
    source: context.source,
    severity,
    feature: context.feature,
    action: context.action,
    route: context.route,
    method: context.method,
    statusCode: context.statusCode,
    request: context.request,
    user: context.user,
    tags: {
      ...(context.tags || {}),
      ...(http4xxClass ? { failure_classification: http4xxClass } : {}),
      ...(networkFailureClass
        ? { fetch_failure_class: networkFailureClass }
        : {}),
      fetch_method: context.method,
      fetch_url: context.url,
    },
    extra: {
      ...(context.extra || {}),
      durationMs: context.durationMs,
      responseSnippet: context.responseSnippet,
    },
  } as const;

  if (typeof window === "undefined") {
    reportServerError(error, base);
  } else {
    reportClientError(error, base);
  }
}

/**
 * Returns true for transient network errors that are worth retrying
 * (connection refused, DNS failure, fetch abort due to network loss).
 */
function isTransientNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  // TrackedFetchError means we got an HTTP response — not a network error
  if (error instanceof TrackedFetchError) return false;
  if (error.name === "AbortError") return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("load failed") ||
    msg.includes("network") ||
    msg.includes("dns") ||
    msg.includes("econnrefused") ||
    msg.includes("enotfound")
  );
}

export async function trackedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  context: TrackedFetchContext = { source: "trackedFetch" }
): Promise<Response> {
  const method = getRequestMethod(input, init);
  const url = getRequestUrl(input);
  const startedAt = Date.now();
  const fetchImpl = context.fetchImpl || fetch;

  const throwOnHttpError = context.throwOnHttpError ?? true;
  const reportHttpErrors = context.reportHttpErrors ?? true;
  const reportAbortErrors = context.reportAbortErrors ?? false;
  const maxNetworkRetries = context.retryOnNetworkError ?? 0;
  const maxHttpRetries = context.retryOn5xx ?? 0;
  const maxRetries = Math.max(maxNetworkRetries, maxHttpRetries);
  const isSafeMethod = ["GET", "HEAD", "OPTIONS"].includes(method);

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Back off before retries: 1s, 2s, 4s for HTTP 5xx; 500ms, 1.5s for network
    if (attempt > 0) {
      const baseMs = maxHttpRetries > 0 ? 1000 : 500;
      await new Promise((r) => setTimeout(r, baseMs * 2 ** (attempt - 1)));
    }

    try {
      const response = await fetchImpl(input, init);
      const durationMs = Date.now() - startedAt;

      // Retry 5xx for idempotent methods before reporting
      if (response.status >= 500 && isSafeMethod && attempt < maxHttpRetries) {
        continue;
      }

      if (!response.ok && reportHttpErrors) {
        if (
          isBenignFirstPartyHttpOutcome(url, method, response.status) ||
          isBenignElevenLabsDubbingHttpOutcome(url, method, response.status) ||
          isBenignYouTubeDataApiOutcome(url, method, response.status) ||
          isBenignYouTubeAnalyticsApiOutcome(url, method, response.status) ||
          isBenignYouTubeStaticThumbnailHttpOutcome(
            url,
            method,
            response.status
          ) ||
          isBenignXAIImageApiHttpOutcome(url, method, response.status) ||
          isBenignOAuthTokenExchangeOutcome(url, method, response.status) ||
          isBenignPlatformDocsFetchOutcome(url, response.status) ||
          isBenignThirdPartyMeasurementBeaconOutcome(
            url,
            method,
            response.status
          )
        ) {
          return response;
        }
        // Only read response body for 5xx — 4xx are demoted to breadcrumbs
        // and reading the body adds latency for no actionable data.
        const responseSnippet =
          response.status >= 500
            ? await readResponseSnippet(response.clone())
            : undefined;
        const trackedError = new TrackedFetchError({
          message: `Fetch failed (${response.status}) ${method} ${url}`,
          status: response.status,
          url,
          method,
          statusText: response.statusText,
          responseSnippet,
        });

        reportFetchError(trackedError, {
          ...context,
          method,
          url,
          statusCode: response.status,
          durationMs,
          responseSnippet,
          extra: {
            ...(context.extra || {}),
            ...(attempt > 0
              ? { retryAttempt: attempt, maxRetries: maxHttpRetries }
              : {}),
          },
        });

        if (throwOnHttpError) {
          throw trackedError;
        }
      }

      return response;
    } catch (error) {
      lastError = error;

      // Retry transient network errors if retries remain
      if (attempt < maxNetworkRetries && isTransientNetworkError(error)) {
        continue;
      }

      const normalizedError = normalizeError(error);
      // SENTRY FIX: NODE-1NK, NODE-2AY — TimeoutError triple-counted (REGRESSION)
      // Date: 2026-04-15 | Agent: Claude Code
      // Previous attempts:
      //   - 12+ fix cycles in gemini-image-generation.ts (circuit breaker, no-retry,
      //     budget passthrough). None fixed the fetch hook capturing the raw
      //     TimeoutError BEFORE the application catch block processes it.
      // Root cause: AbortSignal.timeout() fires a TimeoutError with name="TimeoutError",
      //   but this check only suppressed name="AbortError". The Gemini image generation
      //   code handles timeouts correctly (cascade to Grok), but the global fetch hook
      //   reports the raw error first, creating NODE-1NK (175 events) and NODE-2AY (14).
      // Fix: Treat TimeoutError identically to AbortError for suppression purposes.
      //   Application code (gemini-image-generation.ts) already reports with proper
      //   context and marks as expected=true.
      // DO NOT: Remove this — it prevents the largest Sentry noise cluster (347 events).
      const isAbortError =
        normalizedError.name === "AbortError" ||
        normalizedError.name === "TimeoutError";
      const durationMs = Date.now() - startedAt;

      // Only report non-TrackedFetchError failures (TrackedFetchError was already
      // reported in the !response.ok block above before being thrown).
      if (
        !(error instanceof TrackedFetchError) &&
        (!isAbortError || reportAbortErrors)
      ) {
        // SENTRY FIX: NODE-268 — TypeError: Failed to fetch (REGRESSION #7)
        // Date: 2026-04-12 | Agent: Nightly Autofix
        // Previous attempts:
        //   - 6 prior resolve/regression cycles addressing mixed-content, deploy strategy,
        //     console hook, and release deploys — none addressed the root cause
        // Root cause: transient network failures (connection drops, WiFi/cellular switches,
        //   pod restarts during rolling deploys) cause TypeError: Failed to fetch at the
        //   network level. RSC prefetch failures are invisible to users (Next.js handles
        //   them silently with fallback navigation) but create noise in Sentry.
        // Fix: suppress reporting for RSC prefetch network errors entirely (user never sees
        //   them), and demote other client-side network errors to "warning" severity since
        //   they're transient environmental failures, not code bugs.
        // DO NOT: suppress ALL network errors — api_call failures may indicate real issues.
        const failureClass = classifyFetchFailureByUrl(url);
        const isClientSide = typeof window !== "undefined";
        const isNetworkError = isTransientNetworkError(normalizedError);

        // RSC prefetch network failures: invisible to users, suppress entirely
        if (isNetworkError && failureClass === "rsc_prefetch") {
          throw error instanceof Error ? error : normalizedError;
        }

        // Third-party analytics / experiment beacons — suppress (NODE-26C, NODE-2F4, NODE-2F6).
        if (isBenignThirdPartyMeasurementBeaconUrl(url)) {
          throw error instanceof Error ? error : normalizedError;
        }

        // SENTRY FIX: NODE-268 — TypeError: Failed to fetch (REGRESSION #8)
        // Date: 2026-04-12 | Agent: Claude Code
        // Previous attempts:
        //   - 7 prior cycles: only demoted navigation class, api_call still error
        // Root cause: mobile network instability causes transient failures on
        //   polling endpoints (GET /api/dubbing/) classified as api_call. These
        //   are environmental, not code bugs.
        // Fix: demote ALL client-side transient network errors to warning
        //   (navigation AND api_call). Server-side network errors remain at
        //   their original severity since those indicate real connectivity issues.
        // DO NOT: suppress entirely — warning-level still lets us track trends.
        const effectiveSeverity = isAbortError
          ? "warning"
          : isNetworkError && isClientSide
            ? "warning"
            : context.severity;

        reportFetchError(normalizedError, {
          ...context,
          severity: effectiveSeverity,
          method,
          url,
          durationMs,
          extra: {
            ...(context.extra || {}),
            retryAttempt: attempt,
            maxRetries: maxRetries,
          },
        });

        // SENTRY FIX: NODE-268 — TypeError: Failed to fetch (REGRESSION #9)
        // Date: 2026-04-13 | Agent: Claude Code
        // Previous attempts:
        //   - 8 prior cycles only demoted severity here in tracked-fetch, but
        //     application-layer catch blocks (useDubbingData.ts) re-reported the
        //     same error at default "error" severity, reopening the Sentry issue.
        // Root cause: double-reporting — trackedFetch reports at "warning", then
        //   re-throws; caller catches and calls reportClientError again at "error".
        // Fix: stamp the error so downstream callers can detect it was already
        //   reported and skip their own reportClientError call.
        // DO NOT: remove this stamp — it is the mechanism that prevents regression.
        const thrown = error instanceof Error ? error : normalizedError;
        (
          thrown as Error & { __trackedFetchReported?: boolean }
        ).__trackedFetchReported = true;
      }

      throw error instanceof Error ? error : normalizedError;
    }
  }

  // Should not reach here, but satisfy TypeScript
  throw lastError;
}

export function trackedFetchWithDefaults(
  context: TrackedFetchContext
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  return (input, init) =>
    trackedFetch(input, init, {
      ...context,
      extra: sanitizeUnknown(context.extra || {}) as Record<string, unknown>,
    });
}
